import { FilterItem } from '@ocean/types/dist/filter'
import { Person } from 'core/people/types'
import { RangePeriod } from 'components/common/BigCalendar/types'
import {
  SpecialUser,
  SimulatorTrainingPersonType,
  SimulatorTrainingSessionType,
  SimulatorTrainingScheduleSession,
  SimulatorTrainingMaintenancePeriod,
} from 'modules/simulatorTrainings/common/types'
import { SimulatorInstructorLedEnrolledPerson } from 'modules/simulatorTrainings/common/types/instructor'

export type SchedulesRouteParams = {
  trainingId: Guid
}

export type ScheduleEditorRouteParams = {
  scheduleId: Guid
}

export type ListSimulatorTrainingSchedulesModel = {
  trainingId: Guid
  includeAll?: boolean // default 'true'
  includePast?: boolean // default 'true'
}

export type ListScheduleSessionsModel = {
  startDate: DateTime
  endDate: DateTime
}

export type SimulatorTrainingEnrollment = {
  userId: Identifier
  enrollmentType: SimulatorTrainingPersonType
  workstationNumber: number
}

export type AddSimulatorTrainingScheduleModel = {
  trainingId: Guid
  sessionDates: SimulatorTrainingSessionDate[]
  enrollments: SimulatorTrainingEnrollment[]
  title: string
}

export type DemandSimulatorTrainingScheduleSessionModel = {
  trainingId: Guid
  enrollments: SimulatorTrainingEnrollment[]
}

export type CreateSimulatorTrainingScheduleSessionsModel = {
  startDate: DateTime
  endDate: DateTime
  name: string
  type: SimulatorTrainingSessionType
}

export type UpdateSimulatorTrainingScheduleSessionsModel =
  CreateSimulatorTrainingScheduleSessionsModel & {
    sessionId: Guid
  }

export type RemoveSimulatorTrainingScheduleSessionsModel = {
  sessionId: Guid
  type: SimulatorTrainingSessionType
}

export type UpdateSimulatorTrainingScheduleSessionsRequest = {
  scheduleId: Guid
  timeZone: TimeZone
  createSessions: CreateSimulatorTrainingScheduleSessionsModel[]
  updateSessions: UpdateSimulatorTrainingScheduleSessionsModel[]
  removeSessions: RemoveSimulatorTrainingScheduleSessionsModel[]
}

export type UpdateSimulatorTrainingScheduleSessionModel = {
  startDate: DateTime
  endDate: DateTime
  name: string
}

export type UpdateSimulatorTrainingEnrollmentsModel = {
  scheduleId: Guid
  enrollments: SimulatorTrainingEnrollment[]
}

export enum ScheduleStatus {
  Upcoming,
  Past,
}

export enum ShowType {
  Own,
  All,
}

export enum BookingMode {
  Schedule,
  OnDemandAccess,
}

export type SchedulesTagFilterItems = {
  status: FilterItem
  showType: FilterItem
}

export type SchedulesTagFilterItemNames = keyof SchedulesTagFilterItems

export type SchedulesTagFilterItem = {
  name: SchedulesTagFilterItemNames
  value: FilterItem['value']
}

export type SimulatorTrainingSchedule = {
  scheduleId: Guid
  trainingId: Guid
  startDate: DateTime
  endDate: DateTime
  scheduledBy: string
  instructors: string[]
  enrollments: number
  vacancies: number
}

export type UserWorkstation = {
  userId: Identifier
  workstationId?: Identifier
}

export type UserWorkstations = Record<string, string | undefined>

export type SimulatorTrainingSessionDate = {
  startDate: DateTime
  endDate: DateTime
  sessionType: SimulatorTrainingSessionType
  name: string
}

export type UpdateSessionParams = {
  subject: string
}

export type UpdateSessionSubject = {
  sessionId: Guid
  subject: string
}

export type ScheduleCreatorData = {
  instructorWorkstations: UserWorkstations
  studentWorkstations: UserWorkstations
  addedStudents: Record<string, Person>
  addedInstructors: Record<string, SpecialUser>
  timeZone?: TimeZone
  subject?: string
  range: RangePeriod
  sessionDates: Record<string, SimulatorTrainingScheduleSession>
  bookingMode: BookingMode
}

export type SessionChanges = {
  addedSessions: Record<string, SimulatorTrainingScheduleSession>
  updatedSessions: Record<string, SimulatorTrainingScheduleSession>
  removedSessions: Record<string, SimulatorTrainingScheduleSession>
}

export type ScheduleEditorData = {
  instructorWorkstations: UserWorkstations
  studentWorkstations: UserWorkstations
  addedStudents: Record<string, SimulatorInstructorLedEnrolledPerson>
  addedInstructors: Record<string, SimulatorInstructorLedEnrolledPerson>
  timeZone?: TimeZone
  subject?: string
  range: RangePeriod | null
  addedSessions: Record<string, SimulatorTrainingScheduleSession>
  updatedSessions: Record<string, SimulatorTrainingScheduleSession>
  removedSessions: Record<string, SimulatorTrainingScheduleSession>
}

export type SchedulesListResponse = SimulatorTrainingSchedule[]

export type ScheduleMaintenanceAndSessionsResponse = {
  sessions: SimulatorTrainingScheduleSession[]
  maintenancePeriods: SimulatorTrainingMaintenancePeriod[]
}

export type CreateSimulatorTrainingScheduleResponse = {
  scheduleId: Guid
}

export type DemandSimulatorTrainingScheduleSessionResponse = {
  scheduleId: Guid
}
